import HttpService from 'ui-home/src/misc/HttpService/HttpService';
import { setJitaEnabledAccount } from 'ui-common/src/utils/LoginActions';
import { removeMenuById } from '../utils/removeMenuByID/removeMenuByID';

const AGENT_MANAGEMENT_NAV = {
  id: 'manage-agentic-identities',
  subMenu: [
    { id: 'agents', link: '/agent' },
    { id: 'service-identity', link: '/agent/service-identity' },
  ],
};

const MY_ACCESS_AGENT_NAV = [
  { id: 'ai-agent-requests', link: '/agent/request' },
  { id: 'ai-my-agentic-identities', link: '/agent/user' },
];

/** Superseded ids — do not keep when merging API children */
const LEGACY_AGENT_CHILD_IDS = new Set(['non-human', 'manage-an-agent']);

/**
 * Ensures Agents + Service Identity, keeps other API children (e.g. nhi-olcm).
 */
function mergeAgentManagementChildren(existingChildren = []) {
  const requiredIds = new Set(AGENT_MANAGEMENT_NAV.subMenu.map((c) => c.id));
  const fromApi = existingChildren.filter(
    (c) => !requiredIds.has(c.id) && !LEGACY_AGENT_CHILD_IDS.has(c.id)
  );
  return [...AGENT_MANAGEMENT_NAV.subMenu, ...fromApi];
}

/**
 * Injects "Manage Agentic Identities" into home before user-management.
 * If parent already exists, merges canonical children with API submenu items.
 * When isAgentRequestEnabled, appends agent items to my-access (deduped).
 */
function injectAgentManagementNav(topLevel, { isAgentRequestEnabled } = {}) {
  if (!topLevel || !Array.isArray(topLevel)) return topLevel || [];

  const homeIndex = topLevel.findIndex((menu) => menu.id === 'home');
  if (homeIndex === -1 || !topLevel[homeIndex].subMenu) return topLevel;

  const subMenu = topLevel[homeIndex].subMenu;
  const existingIndex = subMenu.findIndex(
    (item) => item.id === 'manage-agentic-identities' || item.id === 'agent-management'
  );

  let newSubMenu;
  if (existingIndex !== -1) {
    newSubMenu = subMenu.map((item, i) =>
      i === existingIndex
        ? {
            ...item,
            id: AGENT_MANAGEMENT_NAV.id,
            subMenu: mergeAgentManagementChildren(item.subMenu),
          }
        : item
    );
  } else {
    const userMgmtIndex = subMenu.findIndex((item) => item.id === 'user-management');
    const insertIndex = userMgmtIndex === -1 ? subMenu.length : userMgmtIndex;
    newSubMenu = [
      ...subMenu.slice(0, insertIndex),
      AGENT_MANAGEMENT_NAV,
      ...subMenu.slice(insertIndex),
    ];
  }

  if (isAgentRequestEnabled) {
    newSubMenu = newSubMenu.map((item) => {
      if (item.id !== 'my-access') return item;
      const existingIds = new Set((item.subMenu || []).map((c) => c.id));
      const toAdd = MY_ACCESS_AGENT_NAV.filter((c) => !existingIds.has(c.id));
      return toAdd.length ? { ...item, subMenu: [...(item.subMenu || []), ...toAdd] } : item;
    });
  }

  return topLevel.map((menu, i) => (i === homeIndex ? { ...menu, subMenu: newSubMenu } : menu));
}

export const SIDE_MENU_LOADING = 'SIDE_MENU_LOADING';
export const SIDE_MENU_LOADED = 'SIDE_MENU_LOADED';
export const UPDATE_MENU_FILTER = 'UPDATE_MENU_FILTER';

const sideMenuLoading = () => ({ type: SIDE_MENU_LOADING, loading: true });

const InitializeSideMenu = (featureFlags) => (dispatch) => {
  dispatch(sideMenuLoading());
  const response = HttpService.get('/ECM/navigation/leftNav', { 'X-NOERRORMODAL': true });

  const { isEmergencyRolesUser, isIntegration, isIspmEnabled, isAgentRequestEnabled } =
    featureFlags;

  response.then((res) => {
    let sideMenu = [...res.topLevel];

    if (!isEmergencyRolesUser) {
      sideMenu = removeMenuById(sideMenu, 'request-emergency-access-roles');
    }
    if (isIntegration) {
      sideMenu = removeMenuById(sideMenu, 'design');
    } else {
      sideMenu = removeMenuById(sideMenu, 'integrations');
    }

    if (isIspmEnabled) {
      sideMenu = injectAgentManagementNav(sideMenu, {
        isAgentRequestEnabled,
      });
    }

    // Derive isJitaEnabled from the backend left nav response.
    // If the response contains 'jita-sessions' or 'jit-access', the user is a JITA user.
    const hasJitaNavItem = res.topLevel.some(
      (menu) => menu.id === 'jit-access' || menu.subMenu?.some((sub) => sub.id === 'jita-sessions')
    );
    dispatch(setJitaEnabledAccount(hasJitaNavItem));

    dispatch({
      type: SIDE_MENU_LOADED,
      data: sideMenu,
    });
  });
};

const updateMenuFilter = (menuId, isEnabled) => (dispatch, getState) => {
  const state = getState();
  const currentFilter = state.app?.menuFilters?.[menuId];
  const rawSideMenu = state.app?.rawSideMenu;

  // Only update if conditions are met
  if (rawSideMenu?.length && currentFilter !== isEnabled) {
    dispatch({
      type: UPDATE_MENU_FILTER,
      menuId,
      isEnabled,
    });
  }
};

export { InitializeSideMenu, updateMenuFilter };
